const DISCOVERY_DOC = 'https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest';
const SCOPES = 'https://www.googleapis.com/auth/calendar';
const TOKENS_STORAGE_KEY = 'google_calendar_tokens';

let gapi = null;
let gapiInited = false;

// Multiple Google Calendar client IDs for usage-limit failover.
// Falls back to legacy REACT_APP_GOOGLE_CLIENT_ID if _1 is not set.
const CLIENT_IDS = [
  process.env.REACT_APP_GOOGLE_CLIENT_ID_1 || process.env.REACT_APP_GOOGLE_CLIENT_ID,
  process.env.REACT_APP_GOOGLE_CLIENT_ID_2,
  process.env.REACT_APP_GOOGLE_CLIENT_ID_3,
].filter(Boolean);

let tokenClients = []; // One GIS token client per CLIENT_ID
let tokens = [];       // One OAuth token per CLIENT_ID

// --- Token persistence helpers ---

const saveTokens = () => {
  localStorage.setItem(TOKENS_STORAGE_KEY, JSON.stringify(tokens));
};

const loadTokens = () => {
  try {
    const stored = localStorage.getItem(TOKENS_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
    // Migrate from legacy single-token format
    const legacy = localStorage.getItem('google_calendar_token');
    if (legacy) {
      localStorage.removeItem('google_calendar_token');
      return [JSON.parse(legacy)];
    }
    return [];
  } catch {
    return [];
  }
};

const clearAllTokens = () => {
  tokens = [];
  localStorage.removeItem(TOKENS_STORAGE_KEY);
  localStorage.removeItem('google_calendar_token');
};

const clearTokenAt = (index) => {
  tokens[index] = null;
  saveTokens();
};

const isTokenValid = (token) => {
  if (!token) return false;
  if (token.expires_at && Date.now() >= token.expires_at) return false;
  return true;
};

// --- Retry & failover helpers ---

// Retry a Google Calendar API call with exponential backoff (max ~10s total wait)
const retryWithBackoff = async (fn, maxRetries = 3) => {
  // Cumulative waits: 1s + 2s + 4s = 7s, each with up to 300ms jitter (~10s worst case)
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      const status = error?.status ?? error?.result?.error?.code;
      const message = error?.result?.error?.message || error?.message || '';

      const isRetryable =
        status === 429 ||
        [500, 502, 503, 504].includes(status) ||
        (status === 403 && /usage limits|rate limit/i.test(message));

      if (!isRetryable || attempt === maxRetries) {
        throw error;
      }

      const delay = Math.pow(2, attempt) * 1000 + Math.random() * 300;
      console.warn(`Google Calendar rate-limited. Retrying in ${Math.round(delay)}ms (attempt ${attempt + 1}/${maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

const isUsageLimitError = (error) => {
  const status = error?.status ?? error?.result?.error?.code;
  const message = error?.result?.error?.message || error?.message || '';
  return (status === 403 && /usage limits|rate limit|quota/i.test(message)) || status === 429;
};

// Custom error type so the UI can render a specific message + keep saving the interview record
export class GoogleCalendarQuotaError extends Error {
  constructor(message = 'All Google Calendar clients have reached their usage limits. The interview was saved without a calendar event.') {
    super(message);
    this.name = 'GoogleCalendarQuotaError';
    this.isQuotaError = true;
  }
}

// Try fn across all signed-in clients; fall back to next on usage-limit / auth errors.
// Only the last available client uses retryWithBackoff; earlier ones fail fast to next.
const executeWithFallback = async (fn) => {
  const validIndices = [];
  for (let i = 0; i < CLIENT_IDS.length; i++) {
    if (isTokenValid(tokens[i])) validIndices.push(i);
  }
  if (validIndices.length === 0) {
    throw new Error('Google Calendar authentication expired. Please sign in again.');
  }

  for (let attempt = 0; attempt < validIndices.length; attempt++) {
    const idx = validIndices[attempt];
    const isLast = attempt === validIndices.length - 1;

    gapi.client.setToken(tokens[idx]);

    try {
      return isLast ? await retryWithBackoff(fn) : await fn();
    } catch (error) {
      const status = error?.status ?? error?.result?.error?.code;

      // Auth error — clear this token and try next client
      if (status === 401) {
        console.warn(`Calendar client ${idx + 1} token expired, clearing`);
        clearTokenAt(idx);
        if (!isLast) continue;
      }

      // Usage limit — try next client
      if (isUsageLimitError(error) && !isLast) {
        console.warn(`Calendar client ${idx + 1} hit usage limit, falling back to client ${validIndices[attempt + 1] + 1}`);
        continue;
      }

      // Exhausted all clients on a usage-limit error — surface as a specific error
      if (isUsageLimitError(error) && isLast) {
        throw new GoogleCalendarQuotaError();
      }

      throw error;
    }
  }
};

// --- Public API ---

export const initializeGoogleCalendar = async () => {
  try {
    if (CLIENT_IDS.length === 0) {
      console.warn('Google Calendar: No client IDs configured.');
      return false;
    }

    // Check if GAPI is available (loaded from index.html)
    if (typeof window === 'undefined' || !window.gapi) {
      console.warn('Google Calendar: GAPI not loaded.');
      return false;
    }

    gapi = window.gapi;

    // Load and init GAPI client
    await new Promise((resolve) => {
      gapi.load('client', resolve);
    });

    await gapi.client.init({
      discoveryDocs: [DISCOVERY_DOC],
    });

    gapiInited = true;

    // Restore saved tokens, discard expired ones
    const saved = loadTokens();
    tokens = CLIENT_IDS.map((_, i) => {
      const t = saved[i] || null;
      return isTokenValid(t) ? t : null;
    });

    // Set first valid token on gapi client
    const firstValid = tokens.find(isTokenValid);
    if (firstValid) {
      gapi.client.setToken(firstValid);
    }

    // Check if GIS is available (loaded from index.html)
    if (!window.google?.accounts?.oauth2) {
      console.warn('Google Calendar: GIS not loaded.');
      return false;
    }

    // Create a token client for each configured client ID
    tokenClients = CLIENT_IDS.map((clientId) =>
      window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: SCOPES,
        callback: () => {},
      })
    );

    console.info(`Google Calendar initialized with ${CLIENT_IDS.length} client(s)`);
    return true;
  } catch (error) {
    console.error('Failed to initialize Google Calendar:', error);
    return false;
  }
};

// Sign in with a specific client by index
const signInWithClient = (index) => {
  return new Promise((resolve, reject) => {
    const tc = tokenClients[index];
    if (!tc) { reject(new Error(`Token client ${index + 1} not initialized`)); return; }

    tc.callback = (resp) => {
      if (resp.error !== undefined) {
        console.warn(`Calendar client ${index + 1} sign-in failed:`, resp.error, resp.error_description);
        reject(new Error(`OAuth error: ${resp.error} - ${resp.error_description || 'Unknown error'}`));
      } else {
        const token = gapi.client.getToken();
        if (token && resp.expires_in) {
          token.expires_at = Date.now() + (resp.expires_in * 1000);
        }
        tokens[index] = token;
        saveTokens();
        resolve(resp);
      }
    };

    // Clear current token so this client can request its own
    gapi.client.setToken(null);
    tc.requestAccessToken({ prompt: 'consent' });
  });
};

export const signIn = async (onProgress) => {
  if (tokenClients.length === 0) {
    throw new Error('Google Calendar not initialized. Please check your Client IDs in .env file.');
  }
  if (!gapi || !gapiInited) {
    throw new Error('Google API not loaded properly');
  }

  // Sign in with each configured client sequentially, skipping any that are
  // already signed in (so this can be used as a "top up" for missing clients
  // without re-consenting to the ones already connected).
  let firstAttempted = -1;
  for (let i = 0; i < tokenClients.length; i++) {
    if (isTokenValid(tokens[i])) {
      if (onProgress) onProgress(getSignedInCount(), CLIENT_IDS.length);
      continue;
    }
    if (firstAttempted === -1) firstAttempted = i;
    try {
      await signInWithClient(i);
      if (onProgress) onProgress(getSignedInCount(), CLIENT_IDS.length);
    } catch (error) {
      // The first client we actually attempt must succeed; additional ones are optional
      if (i === firstAttempted) throw error;
      console.warn(`Sign-in for Calendar client ${i + 1} skipped:`, error.message);
      if (onProgress) onProgress(getSignedInCount(), CLIENT_IDS.length);
    }
  }
};

export const signOut = () => {
  if (!gapi?.client) return;

  // Revoke all stored tokens
  for (const token of tokens) {
    if (token?.access_token) {
      window.google?.accounts?.oauth2?.revoke(token.access_token);
    }
  }
  gapi.client.setToken(null);
  clearAllTokens();
};

export const createCalendarEvent = async (eventDetails) => {
  try {
    const event = {
      summary: eventDetails.title,
      description: eventDetails.description,
      start: {
        dateTime: eventDetails.startDateTime,
        timeZone: 'Asia/Karachi',
      },
      end: {
        dateTime: eventDetails.endDateTime,
        timeZone: 'Asia/Karachi',
      },
      attendees: eventDetails.attendees?.map(email => ({ email })) || [],
      reminders: {
        useDefault: false,
        overrides: [
          { method: 'email', minutes: 24 * 60 },
          { method: 'popup', minutes: 10 },
        ],
      },
    };

    const response = await executeWithFallback(() =>
      gapi.client.calendar.events.insert({
        calendarId: 'primary',
        resource: event,
        sendUpdates: 'all'
      })
    );
    return response.result;
  } catch (error) {
    console.error('Error creating calendar event:', error);

    // Check for authentication errors
    if (error.status === 401 || error.message?.includes('invalid authentication credentials')) {
      clearAllTokens();
      gapi.client.setToken(null);
      throw new Error('Google Calendar authentication expired. Please sign in again.');
    }

    throw error;
  }
};

export const updateCalendarEvent = async (eventId, eventDetails) => {
  try {
    const event = {
      summary: eventDetails.title,
      description: eventDetails.description,
      start: {
        dateTime: eventDetails.startDateTime,
        timeZone: 'Asia/Karachi',
      },
      end: {
        dateTime: eventDetails.endDateTime,
        timeZone: 'Asia/Karachi',
      },
      attendees: eventDetails.attendees?.map(email => ({ email })) || [],
    };

    const response = await executeWithFallback(() =>
      gapi.client.calendar.events.update({
        calendarId: 'primary',
        eventId: eventId,
        resource: event,
        sendUpdates: 'all'
      })
    );
    return response.result;
  } catch (error) {
    console.error('Error updating calendar event:', error);

    // Check for authentication errors
    if (error.status === 401 || error.message?.includes('invalid authentication credentials')) {
      clearAllTokens();
      gapi.client.setToken(null);
      throw new Error('Google Calendar authentication expired. Please sign in again.');
    }

    throw error;
  }
};

export const deleteCalendarEvent = async (eventId) => {
  try {
    await executeWithFallback(() =>
      gapi.client.calendar.events.delete({
        calendarId: 'primary',
        eventId: eventId,
        sendUpdates: 'all'
      })
    );
    return true;
  } catch (error) {
    console.error('Error deleting calendar event:', error);

    // Check for authentication errors
    if (error.status === 401 || error.message?.includes('invalid authentication credentials')) {
      clearAllTokens();
      gapi.client.setToken(null);
      throw new Error('Google Calendar authentication expired. Please sign in again.');
    }

    throw error;
  }
};

export const isSignedIn = () => {
  return tokens.some(isTokenValid);
};

// How many clients are currently authenticated (useful for UI feedback)
export const getSignedInCount = () => {
  return tokens.filter(isTokenValid).length;
};

export const getTotalClients = () => {
  return CLIENT_IDS.length;
};